import React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { Eye, EyeOff, Lock } from 'lucide-react';

import {
  Modal,
  Button,
  Input,
  Checkbox,
  Loading,
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Separator,
} from '../../components/ui';
import { UserRolesDisplay, AssignRoleDialog } from '../user-roles';
import {
  editUserSchema,
  type EditUserFormData,
} from '../../shared/lib/validation-schemas';
import { useUpdateUser, useUser } from '../../shared/lib/hooks/use-users';
import { usePermissions } from '../../shared/lib/hooks/use-auth';
import { isApiError } from '../../shared/api';
import type { ValidationError, UpdateUserRequest } from '../../shared/types';

interface EditUserFormProps {
  userId: string | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const EditUserForm: React.FC<EditUserFormProps> = ({
  userId,
  open,
  onOpenChange,
}) => {
  const { t } = useTranslation();
  const { canWriteUsers } = usePermissions();
  const [showPassword, setShowPassword] = React.useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = React.useState(false);
  const [isChangingPassword, setIsChangingPassword] = React.useState(false);
  const [showAssignRoleDialog, setShowAssignRoleDialog] = React.useState(false);

  // Fetch user data
  const { data: user, isLoading: userLoading } = useUser(
    userId || '',
    !!userId && open
  );

  // Update user mutation
  const updateUserMutation = useUpdateUser();

  // Form setup
  const form = useForm<EditUserFormData>({
    resolver: zodResolver(editUserSchema),
    defaultValues: {
      email: '',
      firstName: '',
      lastName: '',
      isActive: true,
      password: '',
      confirmPassword: '',
    },
  });

  // Update form when user data is loaded
  React.useEffect(() => {
    if (user) {
      form.reset({
        email: user.email,
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        isActive: user.isActive,
        password: '',
        confirmPassword: '',
      });
    }
  }, [user, form]);

  // Reset form when modal closes
  React.useEffect(() => {
    if (!open) {
      form.reset();
      setIsChangingPassword(false);
      setShowPassword(false);
      setShowConfirmPassword(false);
      setShowAssignRoleDialog(false);
    }
  }, [open, form]);

  // Handle form submission
  const onSubmit = async (data: EditUserFormData) => {
    if (!userId || !user) return;

    try {
      // Prepare update data - only include changed fields
      const updateData: Partial<UpdateUserRequest> = {};

      // Check if email changed
      if (data.email !== user.email) {
        updateData.email = data.email;
      }

      // Check if first name changed
      if (data.firstName !== (user.firstName || '')) {
        updateData.firstName = data.firstName || undefined;
      }

      // Check if last name changed
      if (data.lastName !== (user.lastName || '')) {
        updateData.lastName = data.lastName || undefined;
      }

      // Check if active status changed
      if (data.isActive !== user.isActive) {
        updateData.isActive = data.isActive;
      }

      // Include password if it's being changed
      if (isChangingPassword && data.password && data.password.length > 0) {
        updateData.password = data.password;
      }

      await updateUserMutation.mutateAsync({
        id: userId,
        data: updateData as UpdateUserRequest,
      });

      // Handle role changes separately if needed
      // Note: This would require additional API endpoints for role management
      // For now, we'll just update the basic user data

      // Reset form and close modal on success
      form.reset();
      setIsChangingPassword(false);
      onOpenChange(false);
    } catch (error) {
      console.error('Update user error:', error);

      if (isApiError(error)) {
        if (
          error.isValidationError &&
          error.response &&
          typeof error.response === 'object' &&
          'data' in error.response &&
          error.response.data &&
          typeof error.response.data === 'object' &&
          'detail' in error.response.data &&
          Array.isArray(error.response.data.detail)
        ) {
          const validationErrors = error.response.data.detail as ValidationError[];

          // Set field-specific errors
          validationErrors.forEach((validationError) => {
            const fieldName = validationError.loc[
              validationError.loc.length - 1
            ] as string;

            // Map backend field names to form field names
            const fieldMapping: Record<string, keyof EditUserFormData> = {
              email: 'email',
              password: 'password',
              first_name: 'firstName',
              last_name: 'lastName',
              is_active: 'isActive',
            };

            const formFieldName =
              fieldMapping[fieldName] || (fieldName as keyof EditUserFormData);

            if (formFieldName && formFieldName in form.getValues()) {
              form.setError(formFieldName, {
                type: 'manual',
                message: validationError.msg,
              });
            } else {
              // If we can't map the field, show as root error
              form.setError('root', {
                type: 'manual',
                message: `${fieldName}: ${validationError.msg}`,
              });
            }
          });
        } else if (error.isValidationError) {
          // Fallback for validation errors without detailed info
          form.setError('root', {
            type: 'manual',
            message: error.message,
          });
        } else if (error.status === 409) {
          // Email already exists
          form.setError('email', {
            type: 'manual',
            message: t('errors.emailExists', 'Email already exists'),
          });
        } else {
          form.setError('root', {
            type: 'manual',
            message: t('errors.api', { message: error.message }),
          });
        }
      } else {
        form.setError('root', {
          type: 'manual',
          message: t('errors.unknown'),
        });
      }
    }
  };

  // Handle modal close
  const handleClose = () => {
    if (!form.formState.isSubmitting) {
      form.reset();
      setIsChangingPassword(false);
      setShowAssignRoleDialog(false);
      onOpenChange(false);
    }
  };

  // Toggle password change mode
  const handleTogglePasswordChange = () => {
    setIsChangingPassword(!isChangingPassword);
    if (!isChangingPassword) {
      // Clear password fields when enabling password change
      form.setValue('password', '');
      form.setValue('confirmPassword', '');
    }
  };

  const isFormLoading =
    form.formState.isSubmitting || updateUserMutation.isPending;
  const isReadOnly = !canWriteUsers;

  if (!userId) {
    return null;
  }

  return (
    <Modal
      open={open}
      onOpenChange={handleClose}
      title={isReadOnly ? t('users.viewUser') : t('users.editUser')}
      size="lg"
      className="max-w-2xl w-full mx-4 sm:mx-auto max-h-[90vh] overflow-y-auto"
    >
      {userLoading ? (
        <div className="flex items-center justify-center py-8">
          <Loading size="lg" text={t('common.loading')} />
        </div>
      ) : user ? (
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            {/* Read-only notice */}
            {isReadOnly && (
              <div className="rounded-md bg-muted p-3 mx-1 sm:mx-0">
                <p className="text-sm text-muted-foreground text-center sm:text-left">
                  {t(
                    'users.readOnlyMode',
                    'You have read-only access to user information'
                  )}
                </p>
              </div>
            )}

            {/* Email Field */}
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('auth.email')} *</FormLabel>
                  <FormControl>
                    <Input
                      type="email"
                      placeholder={t('auth.email')}
                      disabled={isFormLoading || isReadOnly}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* First Name and Last Name */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="firstName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('users.firstName')}</FormLabel>
                    <FormControl>
                      <Input
                        type="text"
                        placeholder={t('users.firstName')}
                        disabled={isFormLoading || isReadOnly}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="lastName"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t('users.lastName')}</FormLabel>
                    <FormControl>
                      <Input
                        type="text"
                        placeholder={t('users.lastName')}
                        disabled={isFormLoading || isReadOnly}
                        {...field}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Password Change Section */}
            {!isReadOnly && (
              <>
                <Separator />
                <div className="space-y-4">
                  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div className="flex-1">
                      <h4 className="text-sm font-medium">
                        {t('users.passwordSection')}
                      </h4>
                      <p className="text-sm text-muted-foreground">
                        {t(
                          'users.passwordSectionDescription',
                          'Leave empty to keep current password'
                        )}
                      </p>
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={handleTogglePasswordChange}
                      disabled={isFormLoading}
                      className="w-full sm:w-auto"
                    >
                      <Lock className="mr-2 h-4 w-4" />
                      {isChangingPassword
                        ? t('common.cancel')
                        : t('users.changePassword')}
                    </Button>
                  </div>

                  {isChangingPassword && (
                    <div className="space-y-4">
                      {/* New Password Field */}
                      <FormField
                        control={form.control}
                        name="password"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>{t('users.newPassword')} *</FormLabel>
                            <FormControl>
                              <div className="relative">
                                <Input
                                  type={showPassword ? 'text' : 'password'}
                                  placeholder={t('users.newPassword')}
                                  disabled={isFormLoading}
                                  className="pr-10"
                                  {...field}
                                />
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                                  onClick={() => setShowPassword(!showPassword)}
                                  disabled={isFormLoading}
                                >
                                  {showPassword ? (
                                    <EyeOff className="h-4 w-4" />
                                  ) : (
                                    <Eye className="h-4 w-4" />
                                  )}
                                  <span className="sr-only">
                                    {showPassword
                                      ? 'Hide password'
                                      : 'Show password'}
                                  </span>
                                </Button>
                              </div>
                            </FormControl>
                            <FormDescription>
                              {t(
                                'users.passwordRequirements',
                                'Password must be at least 8 characters with uppercase, lowercase, number and special character'
                              )}
                            </FormDescription>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      {/* Confirm Password Field */}
                      <FormField
                        control={form.control}
                        name="confirmPassword"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel>
                              {t('users.confirmPassword')} *
                            </FormLabel>
                            <FormControl>
                              <div className="relative">
                                <Input
                                  type={
                                    showConfirmPassword ? 'text' : 'password'
                                  }
                                  placeholder={t('users.confirmPassword')}
                                  disabled={isFormLoading}
                                  className="pr-10"
                                  {...field}
                                />
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="sm"
                                  className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                                  onClick={() =>
                                    setShowConfirmPassword(!showConfirmPassword)
                                  }
                                  disabled={isFormLoading}
                                >
                                  {showConfirmPassword ? (
                                    <EyeOff className="h-4 w-4" />
                                  ) : (
                                    <Eye className="h-4 w-4" />
                                  )}
                                  <span className="sr-only">
                                    {showConfirmPassword
                                      ? 'Hide password'
                                      : 'Show password'}
                                  </span>
                                </Button>
                              </div>
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                  )}
                </div>
                <Separator />
              </>
            )}

            {/* User Roles Management */}
            {user && (
              <UserRolesDisplay
                user={user}
                onAssignRole={() => setShowAssignRoleDialog(true)}
              />
            )}

            {/* Active Status */}
            <FormField
              control={form.control}
              name="isActive"
              render={({ field }) => (
                <FormItem className="flex flex-row items-start space-x-3 space-y-0">
                  <FormControl>
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={field.onChange}
                      disabled={isFormLoading || isReadOnly}
                    />
                  </FormControl>
                  <div className="space-y-1 leading-none">
                    <FormLabel>{t('users.isActive')}</FormLabel>
                    <FormDescription>
                      {t(
                        'users.isActiveDescription',
                        'User can log in and access the system'
                      )}
                    </FormDescription>
                  </div>
                </FormItem>
              )}
            />

            {/* Form-level Error Display */}
            {form.formState.errors.root && (
              <div className="rounded-md bg-destructive/15 p-3 mx-1 sm:mx-0">
                <p className="text-sm text-destructive text-center sm:text-left break-words">
                  {form.formState.errors.root.message}
                </p>
              </div>
            )}

            {/* Form Actions */}
            <div className="flex flex-col sm:flex-row justify-end gap-3 pt-4 border-t">
              <Button
                type="button"
                variant="outline"
                onClick={handleClose}
                disabled={isFormLoading}
                className="w-full sm:w-auto order-2 sm:order-1"
              >
                {isReadOnly ? t('common.close') : t('common.cancel')}
              </Button>
              {!isReadOnly && (
                <Button
                  type="submit"
                  disabled={isFormLoading}
                  className="w-full sm:w-auto order-1 sm:order-2"
                >
                  {isFormLoading ? (
                    <div className="flex items-center justify-center">
                      <Loading size="sm" className="mr-2" text="" />
                      {t('common.loading')}
                    </div>
                  ) : (
                    t('common.save')
                  )}
                </Button>
              )}
            </div>
          </form>
        </Form>
      ) : (
        <div className="flex items-center justify-center py-8">
          <p className="text-muted-foreground">
            {t('users.userNotFound', 'User not found')}
          </p>
        </div>
      )}

      {/* Assign Role Dialog */}
      <AssignRoleDialog
        user={user || null}
        open={showAssignRoleDialog}
        onOpenChange={setShowAssignRoleDialog}
      />
    </Modal>
  );
};
